import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tooltip, MenuItem, Select, FormControl, InputLabel, Divider,
  CircularProgress, Autocomplete, Alert
} from '@mui/material';
import { Add, PictureAsPdf, CheckCircle, Cancel, SwapHoriz, Delete } from '@mui/icons-material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../api';
import { abrirPdfAutenticado, formatearPrecio } from './utils';

const ESTADO_COLORS = {
  pendiente: 'warning',
  aprobado: 'success',
  rechazado: 'error',
  vencido: 'default',
};

const ITEM_VACIO = { descripcion: '', costo: '' };

const Presupuestos = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [presupuestos, setPresupuestos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [vehiculosDb, setVehiculosDb] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [emailAdvertencia, setEmailAdvertencia] = useState(null);
  const [errores, setErrores] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);

  const [form, setForm] = useState({
    cliente: '', telefono: '', email: '', vehiculo: '',
    descripcion: '', observaciones: '', condiciones: '',
    items: [{ ...ITEM_VACIO }],
  });

  const fetchPresupuestos = async () => {
    setLoading(true);
    try {
      const params = filtroEstado ? `?estado=${filtroEstado}` : '';
      const res = await api.get(`${process.env.REACT_APP_API_URL}/api/presupuestos/${params}`);
      setPresupuestos(res.data);
    } catch (e) {
      console.error('Error cargando presupuestos', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPresupuestos(); }, [filtroEstado]);

  useEffect(() => {
    api.get(`${process.env.REACT_APP_API_URL}/api/clientes/`)
      .then(res => setClientes(res.data))
      .catch(e => console.error('Error cargando clientes', e));
    api.get(`${process.env.REACT_APP_API_URL}/api/vehiculos/`)
      .then(res => setVehiculosDb(res.data))
      .catch(e => console.error('Error cargando vehículos', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalForm = form.items.reduce((s, i) => s + (parseFloat(i.costo) || 0), 0);

  const handleItemChange = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { ...ITEM_VACIO }] }));

  const removeItem = (idx) => setForm(prev => ({
    ...prev, items: prev.items.filter((_, i) => i !== idx)
  }));

  const resetForm = () => {
    setForm({ cliente: '', telefono: '', email: '', vehiculo: '', descripcion: '', observaciones: '', condiciones: '', items: [{ ...ITEM_VACIO }] });
    setErrores(null);
    setEmailAdvertencia(null);
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setErrores(null);
    try {
      const payload = {
        ...form,
        items: form.items.filter(i => i.descripcion.trim()),
      };
      const res = await api.post(`${process.env.REACT_APP_API_URL}/api/presupuestos/`, payload);
      if (res.data.email_advertencia) {
        setEmailAdvertencia(res.data.email_advertencia);
      } else {
        setOpenForm(false);
        resetForm();
      }
      fetchPresupuestos();
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        const msgs = Object.entries(data).flatMap(([campo, errs]) =>
          (Array.isArray(errs) ? errs : [errs]).map(m => `• ${campo}: ${m}`)
        );
        setErrores(msgs);
      } else {
        setErrores(['Error inesperado al guardar.']);
      }
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (id, estado) => {
    try {
      await api.patch(`${process.env.REACT_APP_API_URL}/api/presupuestos/${id}/cambiar_estado/`, { estado });
      fetchPresupuestos();
    } catch (e) { console.error(e); }
  };

  const elevarAOrden = (presupuesto) => {
    navigate('/ordenes/nueva', { state: { presupuesto } });
  };

  const eliminar = async (id) => {
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/api/presupuestos/${id}/`);
      fetchPresupuestos();
    } catch (e) { console.error(e); }
    finally { setConfirmEliminar(null); }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.6, 0.01, 0.05, 0.95] }}
      >
        <Typography className="titulo-app" variant="h5" gutterBottom>
          <RequestQuoteIcon sx={{ mr: 1, fontSize: 32, color: '#083a94', verticalAlign: 'middle' }} />
          Presupuestos / Cotizaciones
        </Typography>
      </motion.div>

      {emailAdvertencia && (
        <Alert
          severity="warning"
          onClose={() => { setEmailAdvertencia(null); setOpenForm(false); resetForm(); }}
          sx={{ mb: 3 }}
        >
          {emailAdvertencia}
        </Alert>
      )}

      {/* Filtros y acción */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filtroEstado} label="Estado" onChange={e => setFiltroEstado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="pendiente">Pendiente</MenuItem>
            <MenuItem value="aprobado">Aprobado</MenuItem>
            <MenuItem value="rechazado">Rechazado</MenuItem>
            <MenuItem value="vencido">Vencido</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpenForm(true)}>
          Nuevo Presupuesto
        </Button>
      </Box>

      {/* Tabla */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#083a94' }}>
              {['#', 'Cliente', 'Vehículo', 'Total', 'Emisión', 'Vence', 'Estado', 'Acciones'].map(h => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : presupuestos.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No hay presupuestos.</TableCell></TableRow>
            ) : presupuestos.map(p => (
              <TableRow key={p.id} hover>
                <TableCell>{p.id}</TableCell>
                <TableCell>{p.cliente}</TableCell>
                <TableCell>{p.vehiculo}</TableCell>
                <TableCell>${formatearPrecio(p.total)}</TableCell>
                <TableCell>{p.fecha_emision}</TableCell>
                <TableCell>{p.fecha_vencimiento}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={p.estado} color={ESTADO_COLORS[p.estado]} size="small" />
                    {p.orden_generada && (
                      <Chip label="Orden generada" size="small" variant="outlined" color="secondary" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {p.pdf_url && (
                      <Tooltip title="Ver PDF">
                        <IconButton size="small" color="primary" onClick={() => abrirPdfAutenticado(p.pdf_url, api)}>
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {p.estado === 'pendiente' && (
                      <>
                        <Tooltip title="Marcar aprobado">
                          <IconButton size="small" color="success" onClick={() => cambiarEstado(p.id, 'aprobado')}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Marcar rechazado">
                          <IconButton size="small" color="error" onClick={() => cambiarEstado(p.id, 'rechazado')}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {p.estado === 'aprobado' && !p.orden_generada && (
                      <Tooltip title="Elevar a Orden de Servicio">
                        <IconButton size="small" color="secondary" onClick={() => elevarAOrden(p)}>
                          <SwapHoriz fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => setConfirmEliminar(p)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Nuevo Presupuesto */}
      <Dialog open={openForm} onClose={() => { setOpenForm(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo Presupuesto</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Datos del cliente</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Autocomplete
              id="presupuesto-cliente"
              options={clientes}
              getOptionLabel={(option) => option?.nombre || ''}
              value={clientes.find(c => c.nombre === form.cliente) || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  setForm(p => ({ ...p, cliente: newValue.nombre, telefono: newValue.telefono, email: newValue.correo || '' }));
                } else {
                  setForm(p => ({ ...p, cliente: '', telefono: '', email: '' }));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Cliente *" placeholder="Buscar cliente por nombre..." size="small" />
              )}
            />
            <TextField label="Teléfono" value={form.telefono} size="small" fullWidth disabled />
            <TextField label="Email (para envío)" value={form.email} size="small" fullWidth disabled />
            <Autocomplete
              id="presupuesto-vehiculo"
              options={vehiculosDb}
              getOptionLabel={(option) => option?.placa || ''}
              value={vehiculosDb.find(v => v.placa === form.vehiculo) || null}
              onChange={(e, newValue) => {
                setForm(p => ({ ...p, vehiculo: newValue ? newValue.placa : '' }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Placa del vehículo *" placeholder="Buscar por placa..." size="small" />
              )}
            />
          </Box>

          <TextField label="Descripción del trabajo *" value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} multiline rows={2} size="small" fullWidth sx={{ mb: 2 }} />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Ítems del presupuesto</Typography>

          {form.items.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField
                label={`Descripción #${idx + 1}`}
                value={item.descripcion}
                onChange={e => handleItemChange(idx, 'descripcion', e.target.value)}
                size="small"
                sx={{ flex: 3 }}
              />
              <TextField
                label="Costo $"
                type="number"
                value={item.costo}
                onChange={e => handleItemChange(idx, 'costo', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
              />
              {form.items.length > 1 && (
                <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button size="small" onClick={addItem} sx={{ mb: 2 }}>+ Agregar ítem</Button>

          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Total estimado: ${formatearPrecio(totalForm)}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />
          <TextField
            label="Condiciones (opcional)"
            value={form.condiciones}
            onChange={e => setForm(p => ({ ...p, condiciones: e.target.value }))}
            multiline rows={2} size="small" fullWidth sx={{ mb: 1 }}
            placeholder="Precios sujetos a disponibilidad de repuestos. Válido por 60 días."
          />
          <TextField
            label="Observaciones internas (no aparecen en el PDF)"
            value={form.observaciones}
            onChange={e => setForm(p => ({ ...p, observaciones: e.target.value }))}
            multiline rows={2} size="small" fullWidth
          />

          {errores && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <strong>Corrija los siguientes errores:</strong>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>{errores.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenForm(false); resetForm(); }}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar} disabled={guardando}>
            {guardando ? <CircularProgress size={20} /> : 'Guardar y enviar correo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar eliminar */}
      <Dialog open={!!confirmEliminar} onClose={() => setConfirmEliminar(null)}>
        <DialogTitle>Eliminar Presupuesto</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar el presupuesto #{confirmEliminar?.id} de <strong>{confirmEliminar?.cliente}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEliminar(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={() => eliminar(confirmEliminar.id)}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Presupuestos;
